/**
 * GLSL shaders for particle-based demon entity.
 *
 * Fire-like lifecycle: particles spawn at base, rise upward, fade, respawn.
 * Noise sampled from a baked 3D texture (no per-pixel hash computation).
 *
 * Dark body layer: NormalBlending — near-black particles that darken/obscure background
 * Glow layer: AdditiveBlending — embers and eyes that add bright color on top
 */

import { NOISE_TEX_SAMPLE_GLSL } from './noiseTexture'

// ─── Shared GLSL: fire lifecycle + noise texture ──────────────────────

const FIRE_LIFECYCLE_CHUNK = /* glsl */ `
  ${NOISE_TEX_SAMPLE_GLSL}

  // Fire lifecycle: each particle cycles through spawn → rise → fade → respawn
  // Returns: xyz = position offset, w = lifecycle alpha (0 at spawn/death, 1 at peak)
  vec4 fireLifecycle(vec3 basePos, float random, float time, float state, float stateProgress, vec3 velocity) {
    // Cycle duration varies per particle (0.8 - 2.0 seconds)
    float cycleDur = 0.8 + random * 1.2;

    // Each particle has a unique phase offset so they don't all sync
    float life = fract((time + random * cycleDur * 7.0) / cycleDur);

    // Rise speed and wander depend on role (passed via basePos.y height)
    float riseSpeed = 1.2 + random * 0.8;
    float riseY = life * riseSpeed;

    // Horizontal wander from noise texture (cheap lookup, not computed)
    vec3 noiseCoord = vec3(random, life * 0.5, time * 0.1);
    vec3 noiseSample = sampleNoise3D(noiseCoord);
    float wanderX = (noiseSample.r - 0.5) * 0.6;
    float wanderZ = (noiseSample.g - 0.5) * 0.6;

    // Alpha envelope: fade in quickly, hold, fade out
    // 0..0.1 = fade in, 0.1..0.7 = full, 0.7..1.0 = fade out
    float fadeIn = smoothstep(0.0, 0.1, life);
    float fadeOut = 1.0 - smoothstep(0.7, 1.0, life);
    float alpha = fadeIn * fadeOut;

    vec3 offset = vec3(wanderX, riseY, wanderZ);

    // ── State modifiers ──

    // ATTACK: faster cycle, more aggressive rise, outward bursts
    if (state > 0.5 && state < 1.5) {
      float burstPhase = fract(time * 3.0 + random);
      float burst = sin(burstPhase * 3.14159) * 0.8;
      offset += normalize(velocity + 0.001) * burst;
      offset.y *= 1.5; // taller flames when attacking
    }
    // DEATH: particles scatter outward and upward, fade globally
    else if (state > 1.5 && state < 2.5) {
      float deathT = clamp(stateProgress, 0.0, 1.0);
      offset += velocity * deathT * 3.0;
      offset.y += deathT * 2.0;
      alpha *= 1.0 - deathT;
    }
    // MOVING: wisps trail backward
    else if (state > 2.5) {
      offset.z -= life * 0.5;
    }

    return vec4(offset, alpha);
  }
`

// ─── Dark body layer (NormalBlending) ─────────────────────────────────

export const demonBodyVertexShader = /* glsl */ `
  precision highp float;

  attribute vec3 aOffset;
  attribute vec3 aVelocity;
  attribute float aRole;
  attribute float aRandom;
  attribute float aSize;

  uniform float uTime;
  uniform float uState;
  uniform float uStateProgress;

  varying float vRole;
  varying float vAlpha;
  varying float vLife;

  ${FIRE_LIFECYCLE_CHUNK}

  void main() {
    vRole = aRole;
    float t = uTime;
    float r = aRandom;

    // Fire lifecycle
    vec4 fire = fireLifecycle(aOffset, r, t, uState, uStateProgress, aVelocity);
    vec3 localPos = aOffset + fire.xyz;

    vec4 mvPosition = modelViewMatrix * vec4(localPos + position, 1.0);
    gl_Position = projectionMatrix * mvPosition;

    float baseSz = aSize;
    // Particles shrink as they rise (like fire tips)
    float cycleDur = 0.8 + r * 1.2;
    float life = fract((t + r * cycleDur * 7.0) / cycleDur);
    vLife = life;
    baseSz *= 1.0 - life * 0.4;

    gl_PointSize = baseSz * (300.0 / -mvPosition.z);
    gl_PointSize = clamp(gl_PointSize, 1.0, 64.0);

    // Only body + wisp particles for this pass
    vAlpha = fire.w;
    if (aRole < 0.5) vAlpha *= 0.65;       // body: dense
    else if (aRole < 1.5) vAlpha *= 0.25;  // wisp: ethereal
    else vAlpha = 0.0;                       // hide ember/eye in this pass
  }
`

export const demonBodyFragmentShader = /* glsl */ `
  precision highp float;

  uniform float uTime;

  varying float vRole;
  varying float vAlpha;
  varying float vLife;

  void main() {
    if (vAlpha < 0.01) discard;

    float dist = length(gl_PointCoord - vec2(0.5));
    float alpha;

    if (vRole < 0.5) {
      // Body: soft smoky edge
      alpha = 1.0 - smoothstep(0.0, 0.5, dist);
      alpha = pow(alpha, 1.5);
    } else {
      // Wisp: very soft
      alpha = 1.0 - smoothstep(0.0, 0.5, dist);
      alpha = pow(alpha, 0.8);
    }

    alpha *= vAlpha;
    if (alpha < 0.01) discard;

    // Near-black body, slight purple at flame tips (higher life)
    vec3 color;
    if (vRole < 0.5) {
      color = mix(vec3(0.04, 0.0, 0.03), vec3(0.08, 0.0, 0.06), vLife);
    } else {
      color = vec3(0.10, 0.02, 0.12);
    }

    gl_FragColor = vec4(color, alpha);
  }
`

// ─── Glow layer (AdditiveBlending) ────────────────────────────────────

export const demonGlowVertexShader = /* glsl */ `
  precision highp float;

  attribute vec3 aOffset;
  attribute vec3 aVelocity;
  attribute float aRole;
  attribute float aRandom;
  attribute float aSize;

  uniform float uTime;
  uniform float uState;
  uniform float uStateProgress;

  varying float vRole;
  varying float vAlpha;
  varying float vLife;

  ${FIRE_LIFECYCLE_CHUNK}

  void main() {
    vRole = aRole;
    float t = uTime;
    float r = aRandom;

    // Fire lifecycle (same as body)
    vec4 fire = fireLifecycle(aOffset, r, t, uState, uStateProgress, aVelocity);
    vec3 localPos = aOffset + fire.xyz;

    vec4 mvPosition = modelViewMatrix * vec4(localPos + position, 1.0);
    gl_Position = projectionMatrix * mvPosition;

    float cycleDur = 0.8 + r * 1.2;
    float life = fract((t + r * cycleDur * 7.0) / cycleDur);
    vLife = life;

    float baseSz = aSize;
    // Eyes pulse independently of lifecycle
    if (aRole > 2.5) {
      baseSz *= 1.0 + 0.3 * sin(t * 4.0 + r * 6.2832);
    }
    // Embers shrink as they rise
    if (aRole > 1.5 && aRole < 2.5) {
      baseSz *= 1.0 - life * 0.5;
    }
    gl_PointSize = baseSz * (300.0 / -mvPosition.z);
    gl_PointSize = clamp(gl_PointSize, 1.0, 64.0);

    // Only ember + eye particles glow
    vAlpha = fire.w;
    if (aRole < 1.5) vAlpha = 0.0;         // hide body/wisp
    else if (aRole < 2.5) vAlpha *= 0.9;   // ember
    else vAlpha *= 1.0;                      // eye (not lifecycle-driven)
  }
`

export const demonGlowFragmentShader = /* glsl */ `
  precision highp float;

  uniform float uTime;

  varying float vRole;
  varying float vAlpha;
  varying float vLife;

  void main() {
    if (vAlpha < 0.01) discard;

    float dist = length(gl_PointCoord - vec2(0.5));
    float alpha;

    if (vRole < 2.5) {
      // Ember: hard bright core, fast falloff
      alpha = 1.0 - smoothstep(0.0, 0.35, dist);
      alpha = pow(alpha, 2.0);
    } else {
      // Eye: intense core glow
      alpha = 1.0 - smoothstep(0.0, 0.4, dist);
      alpha = pow(alpha, 3.0);
      alpha += (1.0 - smoothstep(0.0, 0.15, dist)) * 2.0;
    }

    alpha *= vAlpha;
    if (alpha < 0.01) discard;

    vec3 color;
    if (vRole < 2.5) {
      // Ember: orange at base → red-orange at tips
      vec3 baseColor = vec3(1.0, 0.4, 0.0);  // warm orange
      vec3 tipColor = vec3(0.8, 0.1, 0.0);    // dark red
      color = mix(baseColor, tipColor, vLife);
      color *= 0.8 + 0.4 * sin(uTime * 8.0 + vLife * 20.0);
    } else {
      // Eyes: burning red-orange, pulsing
      color = vec3(1.0, 0.13, 0.0);
      color *= 1.2 + 0.3 * sin(uTime * 3.0);
    }

    gl_FragColor = vec4(color * alpha, alpha);
  }
`
